import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { ActivityIndicator, View } from 'react-native';
import { COLORS } from '../constants/config';
import { useAuth } from '../contexts/AuthContext';
import AccessDeniedScreen from '../screens/auth/AccessDeniedScreen';

// =====================================================================
// Screen Imports — All Talents screens
// =====================================================================
import TalentsScreen from '../screens/talents/TalentsScreen';
import SpecializationDetailScreen from '../screens/talents/SpecializationDetailScreen';
import ProjectDetailScreen from '../screens/talents/ProjectDetailScreen';
import AddProjectScreen from '../screens/talents/AddProjectScreen';
import OutputGalleryScreen from '../screens/talents/OutputGalleryScreen';
import AddOutputScreen from '../screens/talents/AddOutputScreen';
import StudentDetailScreen from '../screens/talents/StudentDetailScreen';
import AddTalentActivityScreen from '../screens/talents/AddTalentActivityScreen';
import TalentActivityDetailScreen from '../screens/talents/TalentActivityDetailScreen';
import TeacherDetailScreen from '../screens/talents/TeacherDetailScreen';
import EditTeacherScreen from '../screens/talents/EditTeacherScreen';
import EditStudentScreen from '../screens/talents/EditStudentScreen';
import EditTalentsScreen from '../screens/talents/EditTalentsScreen';
import AddTalentKPIScreen from '../screens/talents/AddTalentKPIScreen';
import TalentKPIDetailScreen from '../screens/talents/TalentKPIDetailScreen';
import AddTalentKPIReadingScreen from '../screens/talents/AddTalentKPIReadingScreen';
import StudentTalentRegisterScreen from '../screens/talents/StudentTalentRegisterScreen';
import SettingsScreen from '../screens/settings/SettingsScreen';

// =====================================================================
// Route Param Types
// =====================================================================
export type MainStackParamList = {
  Talents: undefined;
  SpecializationDetail: { id?: number; name: string; entityName?: string; fromEntities?: boolean; description?: string };
  ProjectDetail: { name: string; id?: number; entityName?: string };
  AddProject: { specializationId: number; projectId?: number };
  OutputGallery: { id?: number; name: string };
  AddOutput: { projectId: number };
  StudentDetail: { id: number; name: string; entityName?: string };
  AddTalentActivity: { projectId?: number; specializationId?: number; activityId?: number };
  TalentActivityDetail: { id: number; entityName?: string };
  AddTalentKPI: { specializationId?: number; projectId?: number; activityId?: number; studentId?: number };
  TalentKPIDetail: { kpiLinkId: number };
  AddTalentKPIReading: { kpiLinkId: number };
  EditTalents: { mode?: 'info' | 'talents' | 'add' | 'schedule'; specId?: number };
  TeacherDetail: { id: number; entityName?: string };
  EditTeacher: { id?: number; specializationId?: number };
  EditStudent: { id?: number; specializationId?: number };
  StudentProfile: undefined;
  CoordinatorProfile: undefined;
  StudentTalentRegister: undefined;
  Settings: undefined;
};

const Stack = createNativeStackNavigator<MainStackParamList>();

const stackScreenOptions = {
  headerShown: false,
  animation: 'none' as const,
};

// =====================================================================
// Talents Stack — All screens
// =====================================================================
function TalentsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Talents" component={TalentsScreen} />
      <Stack.Screen name="SpecializationDetail" component={SpecializationDetailScreen} />
      <Stack.Screen name="ProjectDetail" component={ProjectDetailScreen} />
      <Stack.Screen name="AddProject" component={AddProjectScreen} />
      <Stack.Screen name="OutputGallery" component={OutputGalleryScreen} />
      <Stack.Screen name="AddOutput" component={AddOutputScreen} />
      <Stack.Screen name="StudentDetail" component={StudentDetailScreen} />
      <Stack.Screen name="AddTalentActivity" component={AddTalentActivityScreen} />
      <Stack.Screen name="TalentActivityDetail" component={TalentActivityDetailScreen} />
      <Stack.Screen name="TeacherDetail" component={TeacherDetailScreen} />
      <Stack.Screen name="EditTeacher" component={EditTeacherScreen} />
      <Stack.Screen name="EditStudent" component={EditStudentScreen} />
      <Stack.Screen name="EditTalents" component={EditTalentsScreen} />
      <Stack.Screen name="AddTalentKPI" component={AddTalentKPIScreen} />
      <Stack.Screen name="TalentKPIDetail" component={TalentKPIDetailScreen} />
      <Stack.Screen name="AddTalentKPIReading" component={AddTalentKPIReadingScreen} />
      <Stack.Screen name="StudentProfile" component={StudentDetailScreen} />
      <Stack.Screen name="CoordinatorProfile" component={TeacherDetailScreen} />
      <Stack.Screen name="StudentTalentRegister" component={StudentTalentRegisterScreen} />
      <Stack.Screen name="Settings" component={SettingsScreen} />
    </Stack.Navigator>
  );
}

// =====================================================================
// Main Navigator — top-navbar layout, no drawer
// =====================================================================
export default function MainNavigator() {
  const { isAuthenticated, isLoading, accessDenial } = useAuth();

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  if (accessDenial) {
    return <AccessDeniedScreen />;
  }

  if (!isAuthenticated) {
    // AuthContext handles SSO redirect — just show spinner while it processes
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  return <TalentsStack />;
}
